import Vehicle from "@/data/models/Vehicle";
import { Branch } from "@/data/models/Branch";

/**
 * Represents a device with optional home location and branch information,
 * used for cross-warehouse detection during unloading.
 */
interface DeviceWithLocation {
  homeLocation?: string | null;
  branch?: string | null;
}

/**
 * The warehouse a device belongs to, as a branch number.
 *
 * `homeLocation` is the device's home base and `branch` is where it currently is;
 * a device that never had a home base assigned is treated as belonging to where it
 * is. This is the fallback the vehicle flows, the device dialogs and the device CSV
 * all share, so it lives in one place.
 */
export function getDeviceHomeLocation<T extends DeviceWithLocation>(
  device: T
): string | null | undefined {
  return device.homeLocation ?? device.branch;
}

/**
 * Returns devices whose home warehouse differs from the target branch.
 * Used to prompt confirmation before unloading devices to a warehouse
 * that is not their home base.
 *
 * @param devices - Selected devices to check.
 * @param targetBranchNumber - The branch number of the unloading destination.
 * @returns Devices that belong to a different warehouse than the target.
 */
export function detectCrossWarehouseDevices<T extends DeviceWithLocation>(
  devices: T[],
  targetBranchNumber: string | number
): T[] {
  return devices.filter(device => {
    const deviceHome = getDeviceHomeLocation(device);
    return deviceHome && deviceHome.toString() !== targetBranchNumber.toString();
  });
}

/**
 * Evaluates whether a load/unload operation was fully successful,
 * checking all three update results (client panels, other devices, vehicle document).
 *
 * A result is considered acceptable if either the update succeeded
 * or there were no devices of that type selected (nothing to update).
 *
 * @param isClientPanelsSuccess - Whether client panel updates succeeded.
 * @param isOtherDevicesSuccess - Whether other device updates succeeded.
 * @param isVehicleSuccess - Whether the vehicle document update succeeded.
 * @param selectedClientPanelsCount - Number of selected client panels.
 * @param selectedDeviceOverviewsCount - Number of selected device overviews.
 * @returns true if the entire operation should be considered successful.
 */
export function isLoadUnloadSuccessful(
  isClientPanelsSuccess: boolean,
  isOtherDevicesSuccess: boolean,
  isVehicleSuccess: boolean,
  selectedClientPanelsCount: number,
  selectedDeviceOverviewsCount: number
): boolean {
  return (
    (isClientPanelsSuccess || selectedClientPanelsCount === 0) &&
    (isOtherDevicesSuccess || selectedDeviceOverviewsCount === 0) &&
    isVehicleSuccess
  );
}

/**
 * Produces an updated vehicle devices list after a load or unload operation.
 * Returns a new array — does not mutate the input.
 *
 * @param currentDevices - The vehicle's current device ID list.
 * @param selectedDeviceIds - Device IDs being loaded or unloaded.
 * @param isUnloading - true for unload, false for load.
 * @returns The updated device ID list.
 */
export function buildUpdatedDevicesList(
  currentDevices: string[],
  selectedDeviceIds: string[],
  isUnloading: boolean
): string[] {
  const devices = [...currentDevices];

  for (const id of selectedDeviceIds) {
    if (isUnloading) {
      const index = devices.indexOf(id);
      if (index > -1) {
        devices.splice(index, 1);
      }
    } else if (!devices.includes(id)) {
      devices.push(id);
    }
  }

  return devices;
}

/**
 * Checks whether a vehicle ID already exists (case-insensitive),
 * excluding the current vehicle being edited.
 *
 * @param existingVehicles - All vehicles in the system.
 * @param currentId - Firestore document ID of the vehicle being edited.
 * @param vehicleId - The vehicle ID value to check for duplicates.
 * @returns true if a duplicate exists.
 */
export function isDuplicateVehicleId(
  existingVehicles: Vehicle[],
  currentId: string,
  vehicleId: string
): boolean {
  return existingVehicles.some(
    vehicle =>
      vehicle.id !== currentId &&
      vehicle.vehicleId.toLowerCase() === vehicleId.toLowerCase()
  );
}

/**
 * Checks whether a licence plate already exists (case-insensitive),
 * excluding the current vehicle being edited.
 *
 * @param existingVehicles - All vehicles in the system.
 * @param currentId - Firestore document ID of the vehicle being edited.
 * @param licencePlate - The licence plate value to check for duplicates.
 * @returns true if a duplicate exists.
 */
export function isDuplicateLicencePlate(
  existingVehicles: Vehicle[],
  currentId: string,
  licencePlate: string
): boolean {
  return existingVehicles.some(
    vehicle =>
      vehicle.id !== currentId &&
      vehicle.licencePlate.toLowerCase() === licencePlate.toLowerCase()
  );
}

/**
 * Determines whether a vehicle can be deleted.
 *
 * A vehicle cannot be deleted if:
 * - The form has unsaved edits
 * - It is a new vehicle being created (not yet saved)
 * - The dialog is in read-only mode
 * - The vehicle has an allocated user
 * - The vehicle still has devices loaded
 *
 * @param options - Current state of the vehicle form.
 * @returns true if deletion is allowed.
 */
export function canDeleteVehicle(options: {
  hasDetailsEdited: boolean;
  isCreation: boolean;
  isReadonly: boolean;
  allocatedUser: string | null | undefined;
  devicesCount: number;
}): boolean {
  return !(
    options.hasDetailsEdited ||
    options.isCreation ||
    options.isReadonly ||
    options.allocatedUser ||
    options.devicesCount > 0
  );
}

/**
 * Filters devices by a search query, matching against serial number,
 * inventory number, and QR code (case-insensitive).
 *
 * @param devices - Array of devices to search.
 * @param query - Search string to match.
 * @returns Devices matching the query.
 */
export function filterDevicesBySearchQuery<
  T extends { serialNumber?: string | null; inventoryNumber?: string | null; qrCode?: string | null }
>(devices: T[], query: string): T[] {
  const lowerQuery = query.toLowerCase();

  return devices.filter(device => {
    const serialMatch = (device.serialNumber ?? "").toLowerCase().includes(lowerQuery);
    const inventoryMatch = (device.inventoryNumber ?? "").toLowerCase().includes(lowerQuery);
    const qrMatch = (device.qrCode ?? "").toLowerCase().includes(lowerQuery);

    return serialMatch || inventoryMatch || qrMatch;
  });
}

// ─── Branch Resolution ───────────────────────────────────────────────

/**
 * Resolves a branch Firestore document ID from its display branch number.
 *
 * @param branchNumber - The branch number to look up.
 * @param branches - All branches of the client.
 * @returns The matching branch ID, or null if not found.
 */
export function resolveBranchId(
  branchNumber: string | number | null | undefined,
  branches: Branch[]
): string | null {
  if (!branchNumber) {
    return null;
  }

  const match = branches.find(
    branch => branchNumber.toString() === branch.branchNumber.toString()
  );

  return match?.id ?? null;
}

/**
 * Resolves a display branch number from its Firestore document ID.
 *
 * @param branchId - The branch document ID to look up.
 * @param branches - All branches of the client.
 * @returns The matching branch number, or null if not found.
 */
export function resolveBranchNumber(
  branchId: string | null | undefined,
  branches: Branch[]
): number | null {
  if (!branchId) {
    return null;
  }

  const match = branches.find(
    branch => branchId === branch.id.toString()
  );

  return match?.branchNumber ?? null;
}

/**
 * Resolves a branch display name from its branch number.
 *
 * @param branchNumber - The branch number to look up.
 * @param branches - All branches of the client.
 * @returns The branch name, or an empty string if not found.
 */
export function resolveBranchName(
  branchNumber: string | number | null | undefined,
  branches: Branch[]
): string {
  if (!branchNumber) {
    return "";
  }

  const match = branches.find(
    branch => branchNumber.toString() === branch.branchNumber.toString()
  );

  return match?.name ?? "";
}

// ─── Vehicle Form Validation ─────────────────────────────────────────

/**
 * Checks whether a vehicle's editable fields have been modified
 * relative to the initial/saved state.
 *
 * @param current - Current form state.
 * @param initial - Initial vehicle state from the database.
 * @param currentBranchNumber - Resolved branch number from the current branch picker selection.
 * @returns true if any field differs from the initial state.
 */
export function hasVehicleDetailsChanged(
  current: {
    vehicleId: string;
    vehicleSortValue: number;
    licencePlate: string;
    capacity: number;
  },
  initial: Vehicle,
  currentBranchNumber: number | null
): boolean {
  return (
    current.vehicleId !== initial.vehicleId ||
    current.vehicleSortValue !== initial.vehicleSortValue ||
    current.licencePlate !== initial.licencePlate ||
    currentBranchNumber !== initial.branch ||
    current.capacity !== initial.capacity
  );
}

// ─── Device Selection Logic ──────────────────────────────────────────

/** The two groups produced by {@link partitionDevicesByType}. */
export interface DevicePartition<T> {
  clientPanels: T[];
  deviceOverviews: T[];
}

/**
 * Partitions an array of devices into two groups: those that pass
 * a type guard (HyDry/client panels) and those that don't (device overviews).
 *
 * Returns a named object rather than a tuple: `@typescript-eslint/parser` 2.x cannot parse tuple
 * type annotations against TypeScript 4.x (it reads the pre-TS-4.0 `elementTypes` property), and a
 * single tuple type anywhere in a file makes ESLint fail on that whole file.
 *
 * @param devices - Mixed array of devices.
 * @param isClientPanel - Type guard function.
 * @returns The client panels and the device overviews, as named groups.
 */
export function partitionDevicesByType<T>(
  devices: T[],
  isClientPanel: (device: T) => boolean
): DevicePartition<T> {
  const clientPanels: T[] = [];
  const deviceOverviews: T[] = [];

  for (const device of devices) {
    if (isClientPanel(device)) {
      clientPanels.push(device);
    } else {
      deviceOverviews.push(device);
    }
  }

  return { clientPanels, deviceOverviews };
}

/**
 * Computes whether selection state warrants "select all", "intermediate",
 * or "none" for a checkbox.
 *
 * @param selectedCount - Number of currently selected items.
 * @param totalCount - Total number of selectable items.
 * @returns Object with isAllSelected and isIntermediate flags.
 */
export function computeSelectionState(
  selectedCount: number,
  totalCount: number
): { isAllSelected: boolean; isIntermediate: boolean } {
  return {
    isAllSelected: selectedCount > 0 && selectedCount === totalCount,
    isIntermediate: selectedCount > 0 && selectedCount < totalCount,
  };
}

// ─── Device Filtering for Vehicle Views ──────────────────────────────

/**
 * Determines whether a device should appear in a given vehicle view mode.
 *
 * - LOAD: show devices not assigned to any vehicle
 * - UNLOAD / OVERVIEW: show devices assigned to this vehicle
 *
 * @param device - Device with optional vehicleId.
 * @param vehicleId - The current vehicle's Firestore document ID.
 * @param isLoadingMode - true if currently in LOAD_DEVICES view.
 * @returns true if the device should be displayed for this view.
 */
export function shouldShowDeviceInView(
  device: { vehicleId?: string | null },
  vehicleId: string,
  isLoadingMode: boolean
): boolean {
  if (isLoadingMode) {
    return !device.vehicleId;
  }
  return device.vehicleId === vehicleId;
}

/**
 * Computes the display name for a device (inventory number preferred,
 * serial number fallback for HyDry panels).
 *
 * @param device - Device with serial number and inventory number.
 * @param isHyDryPanel - Whether this device is a HyDry client panel.
 * @returns The display name.
 */
export function getDeviceDisplayName(
  device: { inventoryNumber?: string | null; serialNumber?: string | null },
  isHyDryPanel: boolean
): string {
  const inventoryNumber = device.inventoryNumber;

  if (isHyDryPanel) {
    return inventoryNumber && inventoryNumber !== ""
      ? inventoryNumber
      : device.serialNumber ?? "";
  }

  return inventoryNumber ?? "";
}

// ─── Device Availability ─────────────────────────────────────────────

/**
 * Determines whether a device item should be disabled in the vehicle device list.
 * Disabled when: the entire UI is disabled (moving in progress),
 * the device is assigned to a project, or the device is unavailable.
 *
 * @param isDisableAll - Global disable flag (e.g. during batch update).
 * @param isAssignedToProject - Whether the device is currently assigned to a project.
 * @param isAvailable - Whether the device is available (availability !== deleted/unavailable).
 * @returns true if the device item should be disabled.
 */
export function isDeviceItemDisabled(
  isDisableAll: boolean,
  isAssignedToProject: boolean,
  isAvailable: boolean
): boolean {
  return isDisableAll || isAssignedToProject || !isAvailable;
}

/**
 * Determines whether a device is currently assigned to a project,
 * checking the appropriate field depending on device type.
 *
 * @param device - A client panel (has currentProjectId) or device overview (has currentlyAssigned).
 * @param isClientPanelDevice - Whether this device is a client panel.
 * @returns true if the device is assigned to a project.
 */
export function isDeviceAssignedToProject(
  device: { currentProjectId?: string | null; currentlyAssigned?: boolean },
  isClientPanelDevice: boolean
): boolean {
  if (isClientPanelDevice) {
    return device.currentProjectId !== null && device.currentProjectId !== undefined;
  }
  return device.currentlyAssigned === true;
}

// ─── Vehicle Sort & Priority ─────────────────────────────────────────

/**
 * Sorts devices with client panels first, then alphabetically
 * by inventory number / serial number (case-insensitive).
 *
 * @param devices - Array of devices with type discriminator.
 * @param isClientPanelFn - Function to check if a device is a client panel.
 * @returns A new sorted array (does not mutate the input).
 */
export function sortDevicesForVehicleView<
  T extends { inventoryNumber?: string | null; serialNumber?: string | null }
>(
  devices: T[],
  isClientPanelFn: (device: T) => boolean
): T[] {
  return [...devices].sort((a, b) => {
    const aIsClient = isClientPanelFn(a);
    const bIsClient = isClientPanelFn(b);

    if (aIsClient && !bIsClient) return -1;
    if (!aIsClient && bIsClient) return 1;

    const aKey = (a.inventoryNumber ?? a.serialNumber ?? "").toLowerCase();
    const bKey = (b.inventoryNumber ?? b.serialNumber ?? "").toLowerCase();

    return aKey.localeCompare(bKey);
  });
}

/**
 * Sorts device categories by a priority list of category IDs, with non-priority
 * categories sorted alphabetically by name after the prioritized ones.
 *
 * @param categories - Array of category objects with id and categoryName.
 * @param priorityIds - Ordered list of priority category IDs.
 * @returns A new sorted array (does not mutate the input).
 */
export function sortCategoriesByPriority<T extends { id: string; categoryName: string }>(
  categories: T[],
  priorityIds: string[]
): T[] {
  return [...categories].sort((a, b) => {
    const aIsPriority = priorityIds.includes(a.id);
    const bIsPriority = priorityIds.includes(b.id);

    if (aIsPriority && bIsPriority) {
      return priorityIds.indexOf(a.id) - priorityIds.indexOf(b.id);
    }
    if (aIsPriority) return -1;
    if (bIsPriority) return 1;

    return a.categoryName.localeCompare(b.categoryName);
  });
}

// ─── Button State Logic ──────────────────────────────────────────────

/**
 * Determines the text for the left action button based on the current view state.
 *
 * @param isSelectionMode - Whether the user is in load/unload selection mode.
 * @param numberOfSelectedDevices - Count of currently selected devices.
 * @param labels - Object with label strings for each state.
 * @returns The appropriate button label.
 */
export function getLeftButtonLabel(
  isSelectionMode: boolean,
  numberOfSelectedDevices: number,
  labels: { load: string; finished: string; abort: string }
): string {
  if (!isSelectionMode) {
    return labels.load;
  }
  return numberOfSelectedDevices === 0 ? labels.finished : labels.abort;
}

/**
 * Determines whether the right action button (load/unload) should be enabled.
 *
 * @param isSelectionMode - Whether the user is in load/unload selection mode.
 * @param selectedCount - Number of currently selected devices.
 * @param displayedCount - Number of devices currently displayed.
 * @returns true if the right button should be enabled.
 */
export function areSelectionActionsEnabled(
  isSelectionMode: boolean,
  selectedCount: number,
  displayedCount: number
): boolean {
  return (
    (!isSelectionMode && displayedCount > 0) ||
    selectedCount > 0
  );
}
